import { Link, useLocation } from 'react-router-dom';
import { Leaf, Home, Package, FileText, Settings, Menu, X, Bookmark } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from './ui/sonner';

export const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Acasă', icon: Home },
    { path: '/kit', label: 'Trusa Mea', icon: Package },
    { path: '/saved', label: 'Salvate', icon: Bookmark },
    { path: '/results', label: 'Rețete', icon: FileText },
    { path: '/admin', label: 'Admin', icon: Settings },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-acupedia-stone">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200 no-print">
        <div className="container-acupedia">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-acupedia-teal flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                <Leaf className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-heading text-xl md:text-2xl text-acupedia-teal font-semibold">
                  Acupedia
                </h1>
                <p className="text-xs text-stone-500 -mt-1">Medical Tea Maker</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2 py-2 transition-all duration-200 ${
                      isActive(link.path)
                        ? 'text-acupedia-teal border-b-2 border-acupedia-gold'
                        : 'text-stone-600 hover:text-acupedia-teal'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-stone-600 hover:text-acupedia-teal"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-stone-200 bg-white animate-fade-in">
            <div className="container-acupedia py-4 space-y-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                      isActive(link.path)
                        ? 'bg-acupedia-teal/10 text-acupedia-teal'
                        : 'text-stone-600 hover:bg-stone-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white py-8 mt-auto no-print">
        <div className="container-acupedia">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-acupedia-teal flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" />
              </div>
              <span className="text-stone-600 text-sm">
                © {new Date().getFullYear()} Acupedia. Toate drepturile rezervate.
              </span>
            </div>
            <div className="text-xs text-stone-500 text-center md:text-right max-w-md">
              Conținutul are scop educativ și nu înlocuiește consultul medical.
            </div>
          </div>
        </div>
      </footer>

      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
